import type { SetupFile, SetupImportResult, SetupPreview, SetupSection } from "@vaulted/shared";
import { SETUP_SECTIONS, SETUP_SECTION_LABELS, formatCents } from "@vaulted/shared";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post } from "../../api";
import { Alert, Badge, Button, Checkbox, Panel, PanelHeader, cx } from "../../components/ui";

/**
 * Copy this clinic's setup to another environment (staging → production): download a file here,
 * then on the other one upload it, pick sections, see what would change, and choose item by item.
 */
export function SetupTab() {
  return (
    <div className="flex flex-col gap-6">
      <p className="text-ink-soft">
        Moves your setup between environments, like staging to production. Never clients, visits, charts, payments, staff, clinic devices, or phone numbers. Matching is by name, and nothing is ever deleted.
      </p>
      <ExportPanel />
      <ImportPanel />
    </div>
  );
}

/** "priceCents" → "Price", "startMinutes" → "Start minutes". */
const fieldLabel = (f: string) => {
  const words = f.replace(/Cents$/, "").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
/** Money shown in dollars. */
const fieldValue = (f: string, v: string) => (f.endsWith("Cents") && /^-?\d+$/.test(v) ? formatCents(Number(v)) : v);

function SectionChecks({ value, onChange, only }: { value: SetupSection[]; onChange: (v: SetupSection[]) => void; only?: SetupSection[] }) {
  const list = SETUP_SECTIONS.filter((s) => !only || only.includes(s));
  return (
    <div className="grid gap-x-6 sm:grid-cols-2">
      {list.map((s) => (
        <Checkbox
          key={s}
          label={SETUP_SECTION_LABELS[s].label}
          description={SETUP_SECTION_LABELS[s].description}
          checked={value.includes(s)}
          onChange={(on) => onChange(on ? [...value, s] : value.filter((x) => x !== s))}
        />
      ))}
    </div>
  );
}

function ExportPanel() {
  const [sections, setSections] = useState<SetupSection[]>([...SETUP_SECTIONS]);
  const download = useMutation({
    mutationFn: () => get<SetupFile>(`/api/setup/export?sections=${sections.join(",")}`),
    onSuccess: (file) => {
      const url = URL.createObjectURL(new Blob([JSON.stringify(file, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vaulted-setup-${file.exportedAt.slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
  return (
    <Panel>
      <PanelHeader title="Export" description="Download this clinic's setup as a file." />
      <div className="flex flex-col gap-4 p-5">
        <SectionChecks value={sections} onChange={setSections} />
        {download.error && <Alert>{errorMessage(download.error)}</Alert>}
        <div>
          <Button variant="primary" busy={download.isPending} disabled={!sections.length} onClick={() => download.mutate()}>
            Download setup file
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function ImportPanel() {
  const [file, setFile] = useState<SetupFile | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [sections, setSections] = useState<SetupSection[]>([]);
  const [overwrite, setOverwrite] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<SetupImportResult | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const inFile = file ? SETUP_SECTIONS.filter((s) => file.sections[s]) : [];

  const preview = useMutation({ mutationFn: () => post<SetupPreview>("/api/setup/preview", { file, sections, includeArchived }), onSuccess: () => setOverwrite(new Set()) });
  const run = useMutation({
    mutationFn: () => post<SetupImportResult>("/api/setup/import", { file, sections, overwrite: [...overwrite], includeArchived }),
    onSuccess: (r) => {
      setDone(r);
      preview.reset();
    },
  });

  const read = async (f: File | undefined) => {
    setReadError(null);
    setDone(null);
    preview.reset();
    if (!f) return;
    try {
      const parsed = JSON.parse(await f.text()) as SetupFile;
      if (parsed?.kind !== "vaulted-setup") throw new Error("That isn't a Vaulted setup file.");
      setFile(parsed);
      setSections(SETUP_SECTIONS.filter((s) => parsed.sections[s]));
    } catch (e) {
      setFile(null);
      setReadError(e instanceof SyntaxError ? "That file couldn't be read. Pick the .json file from Export." : (e as Error).message);
    }
  };
  const toggle = (id: string, on: boolean) =>
    setOverwrite((cur) => {
      const next = new Set(cur);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <Panel>
      <PanelHeader title="Import" description="Bring in a setup file from another environment. You'll see what would change first." />
      <div className="flex flex-col gap-4 p-5">
        <label className="inline-flex w-fit cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border border-line bg-white px-4 py-2 font-semibold hover:border-brand">
          Choose setup file (.json)
          <input type="file" accept="application/json,.json" className="sr-only" onChange={(e) => read(e.target.files?.[0])} />
        </label>
        {readError && <Alert>{readError}</Alert>}
        {file && (
          <>
            <p className="text-sm text-ink-soft">
              From <span className="font-semibold">{file.clinicName}</span>, exported {new Date(file.exportedAt).toLocaleString()}.
            </p>
            <SectionChecks value={sections} onChange={(v) => (setSections(v), preview.reset())} only={inFile} />
            {(sections.includes("catalog") || sections.includes("formulary")) && (
              <Checkbox
                label="Also bring archived catalog and formulary items (left out unless ticked)"
                checked={includeArchived}
                onChange={(on) => (setIncludeArchived(on), preview.reset())}
              />
            )}
            <div>
              <Button busy={preview.isPending} disabled={!sections.length} onClick={() => preview.mutate()}>
                See what would change
              </Button>
            </div>
          </>
        )}
        {preview.error && <Alert>{errorMessage(preview.error)}</Alert>}
        {preview.data && (
          <div className="flex flex-col gap-4">
            {preview.data.sections.map((s) => {
              const created = s.items.filter((i) => i.status === "new");
              const changed = s.items.filter((i) => i.status === "changed");
              const same = s.items.filter((i) => i.status === "same").length;
              const ids = changed.map((i) => `${s.section}:${i.key}`);
              const allOn = ids.length > 0 && ids.every((id) => overwrite.has(id));
              return (
                <section key={s.section} className="rounded-[var(--radius-control)] border border-line">
                  <header className="flex flex-wrap items-center justify-between gap-2 border-b border-mist px-4 py-2.5">
                    <h3 className="font-semibold">{SETUP_SECTION_LABELS[s.section].label}</h3>
                    <span className="flex flex-wrap gap-2 text-sm">
                      <Badge tone="ok">{created.length} new</Badge>
                      <Badge tone={changed.length ? "wait" : "off"}>{changed.length} different</Badge>
                      <Badge tone="off">{same} the same</Badge>
                      {s.archivedLeftOut > 0 && <Badge tone="off">{s.archivedLeftOut} archived left out</Badge>}
                    </span>
                  </header>
                  {created.length > 0 && (
                    <details className="px-4 py-2 text-sm">
                      <summary className="cursor-pointer font-semibold text-brand">Added: {created.length}</summary>
                      <p className="mt-1 text-ink-soft">{created.map((i) => i.label).join(", ")}</p>
                    </details>
                  )}
                  {changed.length > 0 && (
                    <div className="flex flex-col gap-2 border-t border-mist px-4 py-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm text-ink-soft">Different here. Tick the ones that should take the file's version; the rest stay as they are.</p>
                        <button type="button" className="text-sm font-semibold text-brand" onClick={() => ids.forEach((id) => toggle(id, !allOn))}>
                          {allOn ? "Keep all as they are" : "Use the file's for all"}
                        </button>
                      </div>
                      {changed.map((i) => {
                        const id = `${s.section}:${i.key}`;
                        return (
                          <div key={i.key} className={cx("rounded-[var(--radius-control)] border px-3 py-2", overwrite.has(id) ? "border-brand bg-brand/5" : "border-line")}>
                            <Checkbox label={`${i.label}: use the file's version`} checked={overwrite.has(id)} onChange={(on) => toggle(id, on)} />
                            <table className="mt-1 w-full text-xs">
                              <thead>
                                <tr className="text-left text-muted">
                                  <th className="w-1/5 py-0.5 font-semibold">What</th>
                                  <th className="w-2/5 py-0.5 font-semibold">Here now</th>
                                  <th className="w-2/5 py-0.5 font-semibold">In the file</th>
                                </tr>
                              </thead>
                              <tbody>
                                {i.changes.map((c) => (
                                  <tr key={c.field} className="align-top">
                                    <td className="py-0.5 pr-2 font-semibold">{fieldLabel(c.field)}</td>
                                    <td className="break-all py-0.5 pr-2 text-ink-soft">{fieldValue(c.field, c.here)}</td>
                                    <td className="break-all py-0.5">{fieldValue(c.field, c.file)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
            {run.error && <Alert>{errorMessage(run.error)}</Alert>}
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="primary" busy={run.isPending} onClick={() => run.mutate()}>
                Import
              </Button>
              <span className="text-sm text-muted">
                Adds what's new{overwrite.size ? `, and updates the ${overwrite.size} you ticked` : ""}. Nothing is deleted.
              </span>
            </div>
          </div>
        )}
        {done && (
          <Alert tone="ok">
            Imported: {done.created} added, {done.updated} updated, {done.kept} left as they were, {done.unchanged} already the same.
            {done.notes.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-sm">
                {done.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </div>
    </Panel>
  );
}
